<template>
  <div class="skeleton-wrap" :class="mode" :style="{ borderRadius }">
    <!-- row-based: bar rows, each a shimmer bar -->
    <template v-if="mode === 'bar'">
      <div
        v-for="(row, i) in rowList"
        :key="i"
        class="skeleton-row"
        :style="{
          width: typeof row === 'number' ? row + '%' : row,
          height: rowHeight + 'px',
          marginBottom: gap + 'px',
          borderRadius: barRadius,
        }"
      >
        <div class="skeleton-shimmer"></div>
      </div>
      <div v-if="circle" class="skeleton-circle">
        <div class="skeleton-shimmer"></div>
      </div>
    </template>

    <!-- pie-chart mode: one circle + legend bars below -->
    <template v-if="mode === 'pie'">
      <div class="pie-skeleton">
        <div class="skeleton-circle skeleton-circle--pie">
          <div class="skeleton-shimmer"></div>
        </div>
        <div class="pie-legend-skeleton">
          <div v-for="n in 3" :key="n" class="skeleton-row legend-row">
            <div class="skeleton-shimmer"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- card-grid mode: 2x2 card placeholders (for energy overview) -->
    <template v-if="mode === 'card'">
      <div class="card-grid">
        <div v-for="n in 4" :key="n" class="card-skeleton">
          <div class="card-skeleton-row card-skeleton-label"><div class="skeleton-shimmer"></div></div>
          <div class="card-skeleton-row card-skeleton-value"><div class="skeleton-shimmer"></div></div>
        </div>
      </div>
    </template>

    <!-- list mode: header bar + scroll rows (for top10 / carbon rank) -->
    <template v-if="mode === 'list'">
      <div class="list-skeleton">
        <div v-for="n in 5" :key="n" class="list-skeleton-row">
          <div class="skeleton-row list-index"><div class="skeleton-shimmer"></div></div>
          <div class="skeleton-row list-name"><div class="skeleton-shimmer"></div></div>
          <div class="skeleton-row list-num"><div class="skeleton-shimmer"></div></div>
        </div>
      </div>
    </template>

    <!-- mixed mode: pie-left + list-right (for point analysis) -->
    <template v-if="mode === 'mixed'">
      <div class="mixed-skeleton">
        <div class="mixed-pie">
          <div class="skeleton-circle skeleton-circle--pie">
            <div class="skeleton-shimmer"></div>
          </div>
          <div class="pie-legend-skeleton">
            <div v-for="n in 2" :key="n" class="skeleton-row legend-row"><div class="skeleton-shimmer"></div></div>
          </div>
        </div>
        <div class="mixed-list">
          <div v-for="n in 4" :key="n" class="list-skeleton-row">
            <div class="skeleton-row list-name"><div class="skeleton-shimmer"></div></div>
            <div class="skeleton-row list-num" style="width:25%"><div class="skeleton-shimmer"></div></div>
          </div>
        </div>
      </div>
    </template>

    <!-- number-card mode: a large number bar + a small label bar (for enterprise analysis) -->
    <template v-if="mode === 'number-card'">
      <div class="number-card-skeleton">
        <div class="skeleton-row" style="width:40%;height:24px;border-radius:4px;margin:0 auto 6px"><div class="skeleton-shimmer"></div></div>
        <div class="skeleton-row" style="width:55%;height:12px;border-radius:4px;margin:0 auto"><div class="skeleton-shimmer"></div></div>
      </div>
    </template>

    <!-- statistic-card mode: icon + two stat items (for enterprise stat) -->
    <template v-if="mode === 'stat-card'">
      <div class="stat-card-skeleton">
        <div class="stat-card-item">
          <div class="skeleton-circle" style="width:28px;height:28px;margin:0"><div class="skeleton-shimmer"></div></div>
          <div class="stat-card-text">
            <div class="skeleton-row" style="width:60%;height:18px;border-radius:4px"><div class="skeleton-shimmer"></div></div>
            <div class="skeleton-row" style="width:45%;height:10px;border-radius:4px;margin-top:4px"><div class="skeleton-shimmer"></div></div>
          </div>
        </div>
        <div class="stat-card-item">
          <div class="skeleton-circle" style="width:28px;height:28px;margin:0"><div class="skeleton-shimmer"></div></div>
          <div class="stat-card-text">
            <div class="skeleton-row" style="width:60%;height:18px;border-radius:4px"><div class="skeleton-shimmer"></div></div>
            <div class="skeleton-row" style="width:45%;height:10px;border-radius:4px;margin-top:4px"><div class="skeleton-shimmer"></div></div>
          </div>
        </div>
      </div>
    </template>

    <!-- tree mode: search bar + tree rows (for left panel tree) -->
    <template v-if="mode === 'tree'">
      <div class="tree-skeleton">
        <div class="skeleton-row" style="width:100%;height:28px;border-radius:4px;margin-bottom:8px"><div class="skeleton-shimmer"></div></div>
        <div v-for="n in 4" :key="n" class="tree-row-skeleton">
          <div class="skeleton-row" style="width:55%;height:12px;border-radius:3px"><div class="skeleton-shimmer"></div></div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'SkeletonLoader',
  props: {
    mode: {
      type: String,
      default: 'bar',
      validator: v => ['bar','pie','card','list','mixed','number-card','stat-card','tree'].includes(v)
    },
    /** (bar mode only) number of rows or array of widths */
    rows: {
      type: [Number, Array],
      default: 4,
    },
    rowHeight: { type: Number, default: 14 },
    gap: { type: Number, default: 10 },
    borderRadius: { type: String, default: '8px' },
    barRadius: { type: String, default: '4px' },
    /** (bar mode only) show a circle shimmer at the end */
    circle: { type: Boolean, default: false },
  },
  computed: {
    rowList() {
      if (Array.isArray(this.rows)) return this.rows
      const patterns = ['75', '55', '45', '60', '40']
      return Array.from({ length: this.rows }, (_, i) => patterns[i % patterns.length])
    },
  },
}
</script>

<style scoped>
.skeleton-wrap {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px 4px;
  box-sizing: border-box;
  overflow: hidden;
}

.skeleton-row {
  position: relative;
  overflow: hidden;
  background: rgba(42, 98, 255, 0.06);
  border: 1px solid rgba(42, 98, 255, 0.04);
}

.skeleton-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  margin: 0 auto 8px;
  background: rgba(42, 98, 255, 0.06);
  border: 1px solid rgba(42, 98, 255, 0.04);
  flex-shrink: 0;
}

.skeleton-shimmer {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.skeleton-shimmer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(42, 98, 255, 0.04) 10%,
    rgba(42, 98, 255, 0.15) 35%,
    rgba(42, 98, 255, 0.22) 50%,
    rgba(42, 98, 255, 0.15) 65%,
    rgba(42, 98, 255, 0.04) 90%,
    transparent 100%
  );
  animation: shimmer 1.8s linear infinite;
}

@keyframes shimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* pie skeleton */
.pie-skeleton {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}
.skeleton-circle--pie {
  width: 70px;
  height: 70px;
  margin: 0;
}
.pie-legend-skeleton {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.legend-row {
  width: 65%;
  height: 10px;
  border-radius: 3px;
  margin: 0 auto;
}

/* card grid (2x2) */
.card-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 4px 0;
}
.card-skeleton {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px;
  background: rgba(42, 98, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(42, 98, 255, 0.04);
}
.card-skeleton-row {
  position: relative;
  overflow: hidden;
  border-radius: 3px;
}
.card-skeleton-label {
  width: 55%;
  height: 10px;
}
.card-skeleton-value {
  width: 70%;
  height: 18px;
}

/* list skeleton */
.list-skeleton {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 0;
}
.list-skeleton-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
}
.list-index {
  width: 18px;
  height: 14px;
  border-radius: 3px;
  flex-shrink: 0;
}
.list-name {
  flex: 1;
  height: 12px;
  border-radius: 3px;
}
.list-num {
  width: 50px;
  height: 14px;
  border-radius: 3px;
  flex-shrink: 0;
}

/* mixed: pie left + list right */
.mixed-skeleton {
  display: flex;
  gap: 12px;
  height: 100%;
  padding: 4px 0;
}
.mixed-pie {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.mixed-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
}
.mixed-list .list-skeleton-row {
  gap: 8px;
}

/* number-card */
.number-card-skeleton {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 0;
}

/* stat-card (2 items side by side) */
.stat-card-skeleton {
  display: flex;
  gap: 10px;
  padding: 4px 0;
}
.stat-card-item {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background: rgba(42, 98, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(42, 98, 255, 0.04);
}
.stat-card-text {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* tree skeleton */
.tree-skeleton {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
}
.tree-row-skeleton {
  padding: 4px 0 4px 16px;
}
</style>
